import { Route } from "./+types/get-a-quote";
import { redirect, useFetcher, useOutletContext } from "react-router";
import { getFormProps, getInputProps, getSelectProps, getTextareaProps, useForm } from "@conform-to/react";
import { z } from "zod";
import { isValidPhoneNumber } from 'libphonenumber-js/min';
import { getZodConstraint, parseWithZod } from "@conform-to/zod";
import { ErrorList, Field, SelectField, SelectFieldItem, TextareaField } from "#app/components/forms.tsx";
import { quoteSubjectLineOptions, quoteSubjectValues, stateAbbreviations, usStates, useIsPending } from "#app/utils/misc.tsx";
import { StatusButton } from "#app/components/ui/status-button.tsx";
import { HoneypotInputs } from "remix-utils/honeypot/react";
import { checkHoneypot } from "#app/utils/honeypot.server.ts";
import { sendEmail } from "#app/utils/email.server.ts";
import * as E from '@react-email/components';
import { OutletContext } from "#app/root.tsx";
import { useEffect } from "react";
import { useGtm } from "#app/utils/gtm-context.tsx";

export const action = async ({ request }: Route.ActionArgs) => {
    const formData = await request.formData()
    await checkHoneypot(formData)
    const submission = parseWithZod(formData, { schema: zQuoteForm });
    if (submission.status !== "success") {
        return {
            result: submission.reply()
        }
    }

    const response = await sendEmail({
        to: ['contactus@garagedoorrepairllc.com'],
        bcc: ['garage.d.repair@gmail.com', 'info@dailygrinddigital.com'],
        replyTo: submission.value.email,
        subject: "New Quote Request",
        react: <QuoteRequestEmail submissionData={submission.value} />
    })
    return redirect("/quote-success")
}

type QuoteFormProps = {
    fetcherKey?: string;
}

export const urlRegex = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%?=~_|])|(\bwww\.[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%?=~_|])/i;
// Basic North American phone number pattern detection (adjust if needed for more formats)
export const phoneRegex = /(\b(\+?\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}\b)/;
// Common social media platform names (case-insensitive)
export const socialRegex = /\b(whatsapp|facebook|instagram|twitter|linkedin|tiktok|telegram|snapchat)\b/i;
export const zipCodeRegex = /^\d{5}(-\d{4})?$/;
// Solicitation regex of common spam words.
export const solicitationKeywordsRegex = /\b(SEO|lead generation|ranking higher|marketing agency|web design|outsourcing|digital media|budget friendly plan|competitors may be ranking higher|rank higher|keywords|marketing)\b/i;
// --- Zod Schema Definition ---
export const zQuoteForm = z.object({
    firstName: z.string({ required_error: "First name is required" })
        .min(1, "First name cannot be empty")
        .max(100, "First name seems too long"), // Optional: Add max length

    lastName: z.string({ required_error: "Last name is required" })
        .min(1, "Last name cannot be empty")
        .max(100, "Last name seems too long"), // Optional: Add max length

    email: z.string({ required_error: "Email is required" })
        .min(1, "Email cannot be empty")
        .email("Please enter a valid email address")
        .max(254, "Email seems too long"), // Optional: Add max length

    phone: z.string({ required_error: "Phone number is required" })
        .min(10, "Please enter a complete phone number") // Basic length check
        // Use refine with libphonenumber-js for robust validation
        // Assumes US numbers based on Sanford, Florida location
        .refine(phone => isValidPhoneNumber(phone, 'US'), {
            message: "Please enter a valid US phone number"
        }),

    streetAddress: z.string({ required_error: "Street address is required" })
        .min(3, "Street address seems too short")
        .max(150, "Street address is too long"),

    secondaryAddress: z.string()
        .max(100, "Street address line 2 is too long")
        .nullish(), // Optional field (Apt, Suite, etc.) - allows empty string, null, or undefined

    city: z.string({ required_error: "City is required" })
        .min(2, "City seems too short")
        .max(100, "City is too long"),

    state: z.enum(stateAbbreviations, { // Use z.enum with the abbreviations
        required_error: "Please select a state",
        invalid_type_error: "Invalid state selected"
    }),

    zip: z.string({ required_error: "ZIP code is required" })
        .regex(zipCodeRegex, "Invalid ZIP code format (e.g., 12345 or 12345-6789)"),
    
    subject: z.enum(quoteSubjectValues, { required_error: "Please select a service" }),

    message: z.string({ required_error: "Message is required" })
        .min(10, "Please provide at least 10 characters in your message.")
        .max(1000, "Message cannot exceed 1000 characters") // Optional: Limit message length
        // --- Custom Validations for Message Content ---
        .refine(msg => !urlRegex.test(msg), {
            message: "Please remove website links (URLs) from your message."
        })
        .refine(msg => !phoneRegex.test(msg), {
            message: "Please remove phone numbers from your message."
        })
        .refine(msg => !socialRegex.test(msg), {
                message: "Please avoid mentioning specific social media platform names."
        })
        .refine(msg => !solicitationKeywordsRegex.test(msg), {
            message: "No solictation. Please summarize your garage door needs."
        }),
});

export function QuoteForm({
    fetcherKey,
}: QuoteFormProps) {
    const data = useOutletContext<OutletContext>();
    const { trackEvent } = useGtm();
    const fetcher = useFetcher<typeof action>({ key: fetcherKey });
    const isPending = useIsPending()
    const [form, fields] = useForm({
        id: "quote-form",
        constraint: getZodConstraint(zQuoteForm),
        lastResult: fetcher.data?.result,
        onValidate({ formData }) {
            const submission = parseWithZod(formData, { schema: zQuoteForm })
            return submission;
        },
    });

    useEffect(() => {
        if (form.status === "success") {
            // Track the quote submission event once when the form is successfully submitted
            trackEvent('quote_submitted', {
                firstName: fields.firstName.value,
                service: fields.subject.value,
            });
        }
    }, [form.status, fields.firstName.value, fields.subject.value, trackEvent, fetcher.data?.result?.fields]);

	return (
        <fetcher.Form method="POST" action="/resources/get-a-quote" {...getFormProps(form)}>
            <HoneypotInputs />
            <div className="grid md:grid-cols-2 gap-x-5">
                <Field
                labelProps={{ children: "First Name" }}
                inputProps={{
                    ...getInputProps(fields.firstName, { type: "text" }),
                    autoComplete: "given-name",
                }}
                errors={fields.firstName.errors}
                />
                <Field
                labelProps={{ children: "Last Name" }}
                inputProps={{
                    ...getInputProps(fields.lastName, { type: "text" }),
                    autoComplete: "family-name",
                }}
                errors={fields.lastName.errors}
                />
            </div>
            <Field
            labelProps={{ children: "Email" }}
            inputProps={{
                ...getInputProps(fields.email, { type: "email" }),
                autoComplete: "email",
            }}
            errors={fields.email.errors}
            />
            <Field
            labelProps={{ children: "Phone" }}
            inputProps={{
                ...getInputProps(fields.phone, { type: "tel" }),
                autoComplete: "tel",
            }}
            errors={fields.phone.errors}
            />
            <Field
            labelProps={{ children: "Street Address" }}
            inputProps={{
                ...getInputProps(fields.streetAddress, { type: "text" }),
                autoComplete: "address-line1",
            }}
            errors={fields.streetAddress.errors}
            />
            <Field
            labelProps={{ children: "Street Address Line 2 (Optional)" }}
            inputProps={{
                ...getInputProps(fields.secondaryAddress, { type: "text" }),
                autoComplete: "address-line2",
            }}
            errors={fields.secondaryAddress.errors}
            />
            <div className="grid md:grid-cols-3 gap-x-5">
                <Field
                labelProps={{ children: "City" }}
                inputProps={{
                    ...getInputProps(fields.city, { type: "text" }),
                    autoComplete: "address-level2",
                }}
                errors={fields.city.errors}
                />
                <SelectField
                labelProps={{ children: "State" }}
                selectProps={{
                    ...getSelectProps(fields.state, {  }),
                }}
                errors={fields.state.errors}
                >
                    {usStates.map((state) => (
                        <SelectFieldItem value={state.abbreviation} key={state.name}>
                            {state.name}({state.abbreviation})
                        </SelectFieldItem>
                    ))}
                </SelectField>
                <Field
                labelProps={{ children: "ZIP Code" }}
                inputProps={{
                    ...getInputProps(fields.zip, { type: "text" }),
                    autoComplete: "postal-code",
                }}
                errors={fields.zip.errors}
                />
            </div>
            <SelectField
            labelProps={{ children: "What can we help you with?" }}
            selectProps={{
                ...getSelectProps(fields.subject, {  }),
            }}
            errors={fields.subject.errors}
            >
                {quoteSubjectLineOptions.map((option) => (
                    <SelectFieldItem value={option.value} key={option.value}>
                        {option.name}
                    </SelectFieldItem>
                ))}
            </SelectField>
            <TextareaField
            labelProps={{ children: "Message" }}
            textareaProps={{
                ...getTextareaProps(fields.message, {}),
            }}
            errors={fields.message.errors}
            />
            <ErrorList errors={form.errors} id={form.errorId} />
            <StatusButton
                className="w-full"
                status={fetcher.state !== 'idle' ? 'pending' : (form.status ?? 'idle')}
                type="submit"
                disabled={fetcher.state !== 'idle'}
                size="xl"
            >
                Submit
            </StatusButton>
        </fetcher.Form>
	)   
}

// Infer the type from the Zod schema
type QuoteFormData = z.infer<typeof zQuoteForm>;

// Define the props type for the email component
export type QuoteRequestEmailProps = {
    submissionData: QuoteFormData; // Use a clear prop name
};

export function QuoteRequestEmail({ submissionData }: QuoteRequestEmailProps) {
    return (
        <E.Html lang="en" dir="ltr">
            <E.Tailwind
            config={{
                theme: {
                    fontSize: {
                        xs: ["12px", { lineHeight: "16px" }],
                        sm: ["14px", { lineHeight: "20px" }],
                        base: ["16px", { lineHeight: "24px" }],
                        lg: ["18px", { lineHeight: "28px" }],
                        xl: ["20px", { lineHeight: "28px" }],
                        "2xl": ["24px", { lineHeight: "32px" }],
                        "3xl": ["30px", { lineHeight: "36px" }],
                        "4xl": ["36px", { lineHeight: "36px" }],
                        "5xl": ["48px", { lineHeight: "1" }],
                        "6xl": ["60px", { lineHeight: "1" }],
                        "7xl": ["72px", { lineHeight: "1" }],
                        "8xl": ["96px", { lineHeight: "1" }],
                        "9xl": ["144px", { lineHeight: "1" }],
                    },
                    spacing: {
                        px: "1px",
                        0: "0",
                        0.5: "2px",
                        1: "4px",
                        1.5: "6px",
                        2: "8px",
                        2.5: "10px",
                        3: "12px",
                        3.5: "14px",
                        4: "16px",
                        5: "20px",
                        6: "24px",
                        7: "28px",
                        8: "32px",
                        9: "36px",
                        10: "40px",
                        11: "44px",
                        12: "48px",
                        14: "56px",
                        16: "64px",
                        20: "80px",
                        24: "96px",
                        28: "112px",
                        32: "128px",
                        36: "144px",
                        40: "160px",
                        44: "176px",
                        48: "192px",
                        52: "208px",
                        56: "224px",
                        60: "240px",
                        64: "256px",
                        72: "288px",
                        80: "320px",
                        96: "384px",
                    },
                },
            }}
            >
                <E.Container>
                    <h1>
                        <E.Text>New Quote Request Received</E.Text>
                    </h1>
                    <E.Section>
                        <E.Row>
                            <E.Column align="left" className="w-1/2">First Name:</E.Column>
                            <E.Column align="left" className="w-1/2"><E.Text>{submissionData.firstName}</E.Text></E.Column>
                        </E.Row>
                        <E.Row>
                            <E.Column align="left" className="w-1/2">Last Name:</E.Column>
                            <E.Column align="left" className="w-1/2"><E.Text>{submissionData.lastName}</E.Text></E.Column>
                        </E.Row>
                        <E.Row>
                            <E.Column align="left" className="w-1/2">Email:</E.Column>
                            <E.Column align="left" className="w-1/2"><E.Text>{submissionData.email}</E.Text></E.Column>
                        </E.Row>
                        <E.Row>
                            <E.Column align="left" className="w-1/2">Phone:</E.Column>
                            <E.Column align="left" className="w-1/2"><E.Text>{submissionData.phone}</E.Text></E.Column>
                        </E.Row>
                        <E.Row>
                            <E.Column align="left" className="w-1/2">Address:</E.Column>
                            {/* Format address nicely */}
                            <E.Column align="left" className="w-1/2">
                                <E.Text>
                                    {submissionData.streetAddress}<br />
                                    {submissionData.secondaryAddress && <>{submissionData.secondaryAddress}<br /></>}
                                    {submissionData.city}, {submissionData.state} {submissionData.zip}
                                </E.Text>
                            </E.Column>
                        </E.Row>
                        <E.Row>
                            <E.Column align="left" className="w-1/2">Message:</E.Column>
                            <E.Column align="left" className="w-1/2"><E.Text>{submissionData.message}</E.Text></E.Column>
                        </E.Row>
                    </E.Section>
                </E.Container>
            </E.Tailwind>
        </E.Html>
    )
}